import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AppViews, LoginViews, PortfolioRoutesAvailability } from './routesConfig';

/** Valid without a selected site, so their gate must not divert to My Sites. */
const SITE_INDEPENDENT_VIEWS: readonly AppViews[] = [AppViews.ADMIN, AppViews.PORTFOLIO, AppViews.SITE_NATURE_RISK];

type GateRedirectPathParams = {
    readonly routeKey: AppViews;
    readonly isAuthenticated: boolean;
    readonly hasGivenTncConsent: boolean;
    readonly shouldRedirectToProjects: boolean;
    readonly currentCustomerId: string;
    /** Where the user was headed, as pathname + search, so sign-in can return them there. */
    readonly currentLocation: string;
};

/** Where a route's access gate sends a user it turns away. Always a URL, never a route pattern. */
export const getGateRedirectPath = ({
    routeKey,
    isAuthenticated,
    hasGivenTncConsent,
    shouldRedirectToProjects,
    currentCustomerId,
    currentLocation,
}: GateRedirectPathParams): string => {
    if (isAuthenticated && !hasGivenTncConsent) {
        return navRoutesConfig[LoginViews.TERMS_OF_USE_DECLINED];
    }

    if (shouldRedirectToProjects && !SITE_INDEPENDENT_VIEWS.includes(routeKey)) {
        return navRoutesConfig[AppViews.PROJECTS]({ customerId: currentCustomerId });
    }

    return '/?redirectTo=' + encodeURIComponent(currentLocation);
};

type PortfolioRoutesAvailabilityParams = {
    readonly isCustomerAvailable: boolean;
    readonly currentCustomerHasPortfolio: boolean;
};

export const getPortfolioRoutesAvailabilityForCustomer = ({
    isCustomerAvailable,
    currentCustomerHasPortfolio,
}: PortfolioRoutesAvailabilityParams): PortfolioRoutesAvailability => {
    const hasPortfolioDataAccess = isCustomerAvailable && currentCustomerHasPortfolio;

    return {
        // The grid-backed pages are the portfolio pages (DD-5858): portfolio
        // access is the only gate — no internal check, no preview switch.
        portfolioSummary: hasPortfolioDataAccess,
        portfolioSiteExplorer: hasPortfolioDataAccess,
        speciesRiskIndicators: hasPortfolioDataAccess,
        globalNatureRiskSiteList: isCustomerAvailable,
        globalNatureRiskOverview: isCustomerAvailable,
        globalNatureRiskStateOfNature: isCustomerAvailable,
        globalNatureRiskImpactsAndDependencies: isCustomerAvailable,
    };
};

type PortfolioSidebarGroupVisibilityParams = {
    readonly portfolioRoutesAvailability: PortfolioRoutesAvailability;
};

export const shouldShowGlobalNatureRiskGroup = ({ portfolioRoutesAvailability }: PortfolioSidebarGroupVisibilityParams): boolean => {
    return portfolioRoutesAvailability.globalNatureRiskSiteList;
};

export const shouldShowGlobalNaturePerformanceGroup = ({ portfolioRoutesAvailability }: PortfolioSidebarGroupVisibilityParams): boolean => {
    return (
        portfolioRoutesAvailability.portfolioSummary ||
        portfolioRoutesAvailability.portfolioSiteExplorer ||
        portfolioRoutesAvailability.speciesRiskIndicators
    );
};
